import { ReactNode, useEffect } from 'react';
import axios from 'axios';
import env from 'react-dotenv';
import { useAlert, ErrorBoundary } from '../Components/ui';
import { useAuthHeader, useAuthUser, useSignIn, useSignOut } from 'react-auth-kit';
import { Calendar } from 'lucide-react';
import User from '../Models/User';
import datas from '../Assets/data.json';
import { Crypter } from '../Functions/Security/Crypter';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import { getSessionTokens, performLogout, refreshSession, saveSessionTokens, tokenExpiresInMinutes } from '../Functions/session';

/**
 * Coquille applicative principale : Sidebar + Topbar + contenu + barre de statut.
 * Vérifie également la validité de la session au chargement.
 */
const AppLayout = (props: { children: ReactNode }) => {
    const auth = useAuthUser();
    const user: User = JSON.parse(JSON.stringify(auth() || {}));
    const authHeader = useAuthHeader();
    const signOut = useSignOut();
    const signIn = useSignIn();
    const alert = useAlert();

    function logOut() {
        performLogout(signOut, authHeader());
    }

    function verifSession() {
        let config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Abonnes + Crypter.encrypt(user.CodeU),
            headers: {
                'Accept': 'text/plain',
                'Authorization': authHeader(),
            }
        };

        axios.request(config)
            .then(() => { })
            .catch(() => {
                logOut();
                alert.info('Veuillez vous reconnecter', 'Votre session a expiré !');
            });
    }

    useEffect(() => {
        verifSession();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Prolonge la session via POST /api/Auth/Refresh quand le jeton expire
     * dans moins de 3 min (vérification chaque minute, refresh token dédié).
     */
    useEffect(() => {
        const timer = setInterval(() => {
            const s = getSessionTokens();
            if (!s?.RefreshToken || !s?.TokenExpireLe) return;
            if (new Date(s.TokenExpireLe).getTime() - Date.now() > 3 * 60 * 1000) return;

            refreshSession(authHeader())
                .then((res: any) => {
                    const result = res?.Result;
                    // Tolérant : [[user],[habilitations]] ou objet utilisateur direct
                    const u = Array.isArray(result)
                        ? (Array.isArray(result[0]) ? result[0][0] : result[0])
                        : result;
                    if (res?.StatutCode == 200 && u?.token) {
                        saveSessionTokens(u);
                        const habils = Array.isArray(result) && Array.isArray(result[1]) ? result[1] : null;
                        if (habils) localStorage.setItem(datas.Habilitations, Crypter.encrypt(JSON.stringify(habils)));
                        signIn({
                            token: u.token,
                            expiresIn: tokenExpiresInMinutes(u),
                            tokenType: 'Bearer',
                            authState: JSON.parse(JSON.stringify(auth() || {})),
                        });
                    } else {
                        logOut();
                    }
                })
                .catch(() => { /* coupure réseau : nouvelle tentative au prochain tick */ });
        }, 60000);
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const today = new Date().toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });

    return (
        <div className="eb-shell">
            <Sidebar />
            <div className="eb-main">
                <Topbar />
                <main className="eb-content">
                    <ErrorBoundary zone={document.title || undefined}>
                        {props.children}
                    </ErrorBoundary>
                </main>
                <div className="eb-statusbar">
                    <span>
                        <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ backgroundColor: 'var(--eb-success)' }} />
                        {user?.Name} · {user?.CodeU}
                    </span>
                    <span>Filiale : {user?.CodeFiliale}</span>
                    <div className="right">
                        <span>eBCall © {new Date().getFullYear()} · IBG-Alpha Technology</span>
                        <span>
                            <Calendar size={12} className="inline mr-1" />
                            {today}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AppLayout;
